import React from "react";

import { Pencil, Trash2 } from "lucide-react";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import { actionsColumn } from "../../dataTable/dataTableColumns";
import { ADMIN_ROLE_SCOPES } from "../adminRoleScopes";

import type { TFunction } from "i18next";

import type AdminUserRoles from "../../../models/AdminUserRoles";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";
import type { AdminRoleScope } from "../adminRoleScopes.types";
import type { AdminRoleRow } from "./adminRoleColumns.types";

interface AdminRoleColumnsOptions {
  t: TFunction;
  scope: AdminRoleScope;
  onEdit: (row: AdminRoleRow) => void;
  onDelete: (row: AdminRoleRow) => void;
}

const ALL_ROW_ID = "all";

export function getAdminRoleRows(
  scope: AdminRoleScope,
  roles: AdminUserRoles,
  t: TFunction,
): AdminRoleRow[] {
  const config = ADMIN_ROLE_SCOPES[scope];

  if (config.coversAll(roles)) {
    return [
      {
        uuid: ALL_ROW_ID,
        name: t("all"),
        roleList: [],
        rolesLabel: t("editorAdmin"),
        isAll: true,
      },
    ];
  }

  return config.getScopes(roles).map((roleScope) => ({
    uuid: roleScope.uuid,
    name: roleScope.name,
    roleList: roleScope.roleList,
    rolesLabel: roleScope.rolesString,
    isAll: false,
  }));
}

export function adminRoleColumns({
  t,
  scope,
  onEdit,
  onDelete,
}: AdminRoleColumnsOptions): DataTableColumnDef<AdminRoleRow>[] {
  const scopeTitle = t(ADMIN_ROLE_SCOPES[scope].headerKey);

  return [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={scopeTitle} />
      ),
      meta: { label: scopeTitle },
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "rolesLabel",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("roles")} />
      ),
      meta: { label: t("roles") },
      cell: ({ row }) => row.original.rolesLabel,
    },
    actionsColumn<AdminRoleRow>(
      (row) =>
        row.isAll
          ? []
          : [
              {
                label: t("edit"),
                icon: <Pencil />,
                onClick: () => onEdit(row),
              },
              {
                label: t("delete"),
                icon: <Trash2 />,
                variant: "destructive",
                onClick: () => onDelete(row),
                confirm: {
                  title: t("deleteRolesTitle"),
                  description: t("deleteRolesBody", { scope: row.name }),
                  confirmLabel: t("delete"),
                },
              },
            ],
      (row) => row.name,
    ),
  ];
}
